/**
 * Compute the rectangle for each visible cell, accounting for merges.
 * `horizontalGridlines[i]` is the y-coordinate of the i-th horizontal line.
 * `verticalGridlines[j]` is the x-coordinate of the j-th vertical line.
 * A cell at (row, col) is bounded by gridlines [row, row+1] and [col, col+1].
 *
 * @param {number[]} horizontalGridlines
 * @param {number[]} verticalGridlines
 * @param {MergeStore} mergeStore
 * @returns {{x: number, y: number, width: number, height: number}[]}
 */
function computeCellRectangles(
  horizontalGridlines,
  verticalGridlines,
  mergeStore,
) {
  const numRows = horizontalGridlines.length - 1;
  const numCols = verticalGridlines.length - 1;
  const rectangles = [];

  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numCols; col++) {
      const merge = mergeStore?.getMergeAt(row, col);
      const rindex = numRows - row - 1;

      if (merge == null) {
        const x = verticalGridlines[col];
        const y = horizontalGridlines[rindex];
        const width = verticalGridlines[col + 1] - x;
        const height = horizontalGridlines[rindex + 1] - y;

        // Unmerged cell: emit its rectangle directly
        rectangles.push({ row, col, x, y, width, height });
      } else if (merge.row === row && merge.col === col) {
        // Anchor of a merge: emit one rectangle spanning the whole merge
        const x = verticalGridlines[merge.col];
        const hindex = Math.max(
          Math.min(rindex + 1 - merge.rowspan, numRows),
          0,
        );
        const y = horizontalGridlines[hindex];
        const windex = Math.max(
          Math.min(merge.col + merge.colspan, numCols),
          0,
        );
        const width = verticalGridlines[windex] - x;
        const height = horizontalGridlines[rindex + 1] - y;

        rectangles.push({ row, col, x, y, width, height });
      }
      // Otherwise: cell is covered by a merge but isn't the anchor — skip
    }
  }

  return rectangles;
}

export { computeCellRectangles };
